import type { CellTemplateProp, DataType, HyperFunc, VNode } from '@revolist/react-datagrid';

import { RevoGridTableExtras } from './types';

export const REVO_GRID_SWITCH_CLASS = 'sm-switch';
export const REVO_GRID_SWITCH_INPUT_CLASS = 'sm-switch-input';
export const REVO_GRID_SWITCH_TRACK_CLASS = 'sm-switch-track';
export const REVO_GRID_SWITCH_THUMB_CLASS = 'sm-switch-thumb';
export const REVO_GRID_SWITCH_CHECKED_CLASS = 'checked';
export const REVO_GRID_SWITCH_DISABLED_CLASS = 'disabled';

type ToggleCellTemplateConfig = {
  name: string;
  isApplicable?: (model: DataType) => boolean;
};

export const createToggleCellTemplate =
  ({ name, isApplicable }: ToggleCellTemplateConfig) =>
  (h: HyperFunc<VNode>, props: CellTemplateProp, extra?: RevoGridTableExtras) => {
    const model = props.model ?? {};

    // an empty span, not `undefined`: the cell still has to render something
    if (isApplicable && !isApplicable(model)) {
      return h('span');
    }

    const rowId = (model as { id?: string }).id ?? '';
    const checked = Boolean(model[props.prop]);
    const onToggleCell = extra?.onToggleCell;
    const isDisabled = !onToggleCell;

    return h(
      'span',
      { style: { display: 'flex', alignItems: 'center', height: '100%' } },
      h(
        'span',
        {
          class: [
            REVO_GRID_SWITCH_CLASS,
            ...(checked ? [REVO_GRID_SWITCH_CHECKED_CLASS] : []),
            ...(isDisabled ? [REVO_GRID_SWITCH_DISABLED_CLASS] : []),
          ].join(' '),
        },
        [
          h('input', {
            type: 'checkbox',
            role: 'switch',
            class: REVO_GRID_SWITCH_INPUT_CLASS,
            'aria-label': `${name} ${rowId}`,
            'data-sm': `${name}-${rowId}`,
            checked,
            disabled: isDisabled,
            // do not let the switch start a range drag or move the grid focus
            onMouseDown: (event: MouseEvent) => event.stopPropagation(),
            onClick: (event: MouseEvent) => event.stopPropagation(),
            onKeyDown: (event: KeyboardEvent) => event.stopPropagation(),
            onChange: () => onToggleCell?.(rowId, props.prop, !checked),
          }),
          h('span', { class: REVO_GRID_SWITCH_TRACK_CLASS }),
          h('span', { class: REVO_GRID_SWITCH_THUMB_CLASS }),
        ]
      )
    );
  };
